/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

const STORAGE_KEY = 'dcp-sidebar-collapsed'

interface SidebarContextValue {
  /** Whether the sidebar is in collapsed (icon-only) mode */
  isCollapsed: boolean
  /** Toggle sidebar between collapsed and expanded states */
  toggle: () => void
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined)

/**
 * Reads initial collapsed state from localStorage
 */
function getInitialState(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Provider for sidebar collapsed/expanded state.
 * Persists preference to localStorage.
 */
export function SidebarProvider({ children }: { children: ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(getInitialState)

  const toggle = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(STORAGE_KEY, String(next))
      } catch {
        // Ignore storage errors
      }
      return next
    })
  }, [])

  return (
    <SidebarContext.Provider value={{ isCollapsed, toggle }}>
      {children}
    </SidebarContext.Provider>
  )
}

/**
 * Hook to access sidebar collapsed state and toggle function.
 * Must be used within SidebarProvider.
 */
export function useSidebar(): SidebarContextValue {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}
